const HEIGHT_INCREMENT = 1024

export class Atlas {
  constructor({ label, renderer }) {
    this.label = label
    this.width = 1024
    this.height = 1024
    this.pad = 0
    this.slices = []
    this.tileMap = {}
    this.nextId = 1 // truthy no zeros
    this.skyline = [{ x: 0, y: 0, w: this.width }] // initial skyline: one segment full width at y=0
    this.renderer = renderer
    this.renderer?.init(this.width, this.height, this.pad)
  }

  _maxSkylineY() {
    let maxY = 0
    for (let i = 0; i < this.skyline.length; i++) {
      if (this.skyline[i].y > maxY) maxY = this.skyline[i].y
    }
    return maxY
  }

  _growHeight(newHeight) {
    if (newHeight <= this.height) return
    // console.log('Atlas.grow', this.label, this.height, newHeight)
    this.height = newHeight
    this.renderer?.resize(this.width, this.height)
  }

  /** Packs w×h (pixels) and returns pixel rect (x,y,w,h) or null. */
  _pack(w, h) {
    const ALIGN = 8 // 2^(mipLevels-1) for 4 mip levels

    // Round up w and h to alignment so we reserve aligned space
    const alignedW = Math.ceil(w / ALIGN) * ALIGN
    const alignedH = Math.ceil(h / ALIGN) * ALIGN

    let bestY = Infinity,
      bestX = 0,
      bestIdx = -1

    for (let i = 0; i < this.skyline.length; i++) {
      let x = this.skyline[i].x
      // Align x up to next boundary
      let alignedX = Math.ceil(x / ALIGN) * ALIGN

      let y = this.skyline[i].y
      let runW = 0
      let j = i

      // Account for the gap from alignment
      let startOffset = alignedX - x

      while (j < this.skyline.length && runW < alignedW + startOffset) {
        y = Math.max(y, this.skyline[j].y)
        if (y + alignedH > this.height) break
        runW += this.skyline[j].w
        j++
      }

      // Align y as well
      let alignedY = Math.ceil(y / ALIGN) * ALIGN
      if (alignedY + alignedH > this.height) continue

      if (runW >= alignedW + startOffset && alignedY < bestY) {
        bestY = alignedY
        bestX = alignedX
        bestIdx = i
      }
    }

    if (bestIdx < 0) return null

    // Splice skyline using the ALIGNED dimensions
    let remain = bestX + alignedW - this.skyline[bestIdx].x
    let insertY = bestY + alignedH
    let k = bestIdx

    while (remain > 0 && k < this.skyline.length) {
      const seg = this.skyline[k]
      if (!seg) break

      const take = Math.min(remain, seg.w)
      const lenBefore = this.skyline.length

      if (take === seg.w) {
        seg.y = insertY
        this._mergeAround(k)
        remain -= take
        if (this.skyline.length === lenBefore) k++
      } else {
        const tail = { x: seg.x + take, y: seg.y, w: seg.w - take }
        seg.w = take
        seg.y = insertY
        this.skyline.splice(k + 1, 0, tail)
        this._mergeAround(k)
        remain -= take
        k++
      }
    }

    return { x: bestX, y: bestY }
  }

  _mergeAround(i) {
    // merge left
    if (i > 0 && this.skyline[i - 1].y === this.skyline[i].y) {
      this.skyline[i - 1].w += this.skyline[i].w
      this.skyline.splice(i, 1)
      i--
    }
    // merge right
    if (i + 1 < this.skyline.length && this.skyline[i + 1].y === this.skyline[i].y) {
      this.skyline[i].w += this.skyline[i + 1].w
      this.skyline.splice(i + 1, 1)
    }
  }

  /** Insert an Image/Canvas/Video source rect; returns sliceId. */
  insert(filename, sx, sy, sw, sh) {
    // dont insert the same thing multiple times
    const tileId = `${filename}_${sx}_${sy}_${sw}_${sh}`
    if (this.tileMap[tileId]) return this.tileMap[tileId]

    // console.log('Atlas.insert', this.label, filename, sx, sy, sw, sh)

    // console.time('Atlas.insert')

    const W = sw + 2 * this.pad
    const H = sh + 2 * this.pad

    let pos = this._pack(W, H)
    if (!pos) {
      // Not enough height. Decide a new height and grow.
      // Strategy: either double or just-enough, whichever is larger.
      const needed = this._maxSkylineY() + H
      const newHeight = Math.ceil(needed / HEIGHT_INCREMENT) * HEIGHT_INCREMENT
      // const doubled = this.height * 2
      // const newHeight = Math.max(doubled, needed)
      console.error(`TODO: atlas grow height from ${this.height} to ${newHeight}`)
      this._growHeight(newHeight)

      // Try again (no skyline mutation happened when _pack failed)
      pos = this._pack(W, H)
      if (!pos) {
        // extremely rare unless width is the issue
        console.error(`atlas could not insert ${filename} (maybe too wide?)`)
        return null
      }
    }

    const id = this.nextId++

    const dx = pos.x + this.pad
    const dy = pos.y + this.pad

    // Store in IMAGE SPACE (no flip)
    const u0 = dx / this.width
    const v0 = dy / this.height
    const du = sw / this.width
    const dv = sh / this.height

    // const u0 = dx / this.width
    // const du = sw / this.width
    // // bottom-left space because upload is unflipped:
    // const v0 = 1 - (dy + sh) / this.height
    // const dv = sh / this.height

    this.slices.push({ id, u0, v0, du, dv, px: dx, py: dy, pw: sw, ph: sh })

    this.renderer?.draw(id, filename, sx, sy, sw, sh, dx, dy, this.pad, u0, v0, du, dv)

    this.tileMap[tileId] = id

    // console.timeEnd('Atlas.insert')

    return id
  }

  clear() {
    this.slices = []
    this.tileMap = {}
    this.nextId = 1
    this.skyline = [{ x: 0, y: 0, w: this.width }]
    this.renderer?.clear()
    // console.log('Atlas.clear', this.label)
  }
}
